import { BrowserModule } from "@angular/platform-browser";
import { NgModule, ErrorHandler, Injectable, LOCALE_ID } from "@angular/core";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { PortalModule } from "@angular/cdk/portal";
import { VisModule } from "ngx-vis";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { FightLineComponent } from "../pages/fightline/fightline.component";
import { TableViewComponent } from "../pages/tableview/tableview.component";
import { CellComponent } from "../pages/tableview/cell/cell.component";
import { HomeComponent } from "../pages/home/home.component";
import { SidepanelComponent } from "../components/sidepanel/sidepanel.component";
import { PlanAreaComponent } from "../pages/fightline/planArea/planArea.component";
import { FilterComponent } from "../pages/fightline/filter/filter.component";
import { ViewComponent } from "../pages/fightline/view/view.component";
import { SettingsFilterComponent } from "../dialogs/settingsDialog/filter/settingsFilter.component";
import { SettingsViewComponent } from "../dialogs/settingsDialog/view/settingsView.component";
import { ToolbarComponent } from "../components/toolbar/toolbar.component";
import { SyncSettingsComponent } from "../dialogs/bossAttackDialog/syncSettings/syncSettings.component";
import { SyncDowntimeComponent } from "../dialogs/bossAttackDialog/syncDowntime/syncDowntime.component";
import * as Services from "../services/index";
import { JwtInterceptor } from "../interceptors/jwtInterceptor";
import { ClipboardModule } from "ngx-clipboard";
import { NgProgressbar } from "ngx-progressbar";
import { NgxCaptchaModule } from "ngx-captcha";
import { DialogsModuleComponents } from "../dialogs/index";
import { SingleAbilityComponent } from "../components/singleAbility/singleAbility.component";
import { WarningsComponent } from "../components/warnings/warnings.component";
import { SingleAttackComponent } from "../components/singleAttack/singleAttack.component";
import { MultipleAbilityComponent } from "../components/multipleAbility/multipleAbility.component";
import { MultipleAttackComponent } from "../components/multipleAttack/multipleAttack.component";
import { JobComponent } from "../components/job/job.component";
import { JobAbilityComponent } from "../components/jobAbility/jobAbility.component";
import { DownTimeComponent } from "../components/downtime/downtime.component";
import { MultipleDownTimeComponent } from "../components/multipleDowntime/multipleDowntime.component";
import { AreaComponent } from "../components/area/area.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { OffsetWheelDirective } from "../helpers/OffsetWheelDirective";
import { FFLogsMatcherDirective } from "../helpers/FFLogsMatchDirective";
import { CustomScrollDirective } from "../helpers/customScroll.directive";
import { KeyHandlerDirective } from "../helpers/keyHandler.directive";
import { KillsOnlyPipe } from "../helpers/KillsPipe";
import { NoDraftsPipe } from "../helpers/NoDraftsPipe";
import { PingComponent } from "../components/ping/ping.component";
import * as Sentry from "@sentry/browser";
import { AngularSplitModule } from "angular-split";
import { environment } from "../environments/environment";
import { XivapiClientModule } from "@xivapi/angular-client";
import { ColorPickerModule } from "ngx-color-picker";

import { NzAlertModule } from "ng-zorro-antd/alert";

import { NzAutocompleteModule } from "ng-zorro-antd/auto-complete";
import { NzAvatarModule } from "ng-zorro-antd/avatar";

import { NzBadgeModule } from "ng-zorro-antd/badge";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzCardModule } from "ng-zorro-antd/card";
import { NzCarouselModule } from "ng-zorro-antd/carousel";
import { NzCascaderModule } from "ng-zorro-antd/cascader";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzNoAnimationModule } from "ng-zorro-antd/core/no-animation";
import { NzTransButtonModule } from "ng-zorro-antd/core/trans-button";
import { NzWaveModule } from "ng-zorro-antd/core/wave";
import { NzDatePickerModule } from "ng-zorro-antd/date-picker";
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzEmptyModule } from "ng-zorro-antd/empty";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzGridModule } from "ng-zorro-antd/grid";
import {
  en_US,
  NzI18nModule,
  NZ_I18N,
  fr_FR,
  de_DE,
  ja_JP,
  zh_CN,
} from "ng-zorro-antd/i18n";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzInputNumberModule } from "ng-zorro-antd/input-number";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzListModule } from "ng-zorro-antd/list";
import { NzMenuModule } from "ng-zorro-antd/menu";
import { NzMessageModule } from "ng-zorro-antd/message";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzNotificationModule } from "ng-zorro-antd/notification";
import { NzPageHeaderModule } from "ng-zorro-antd/page-header";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";
import { NzPopoverModule } from "ng-zorro-antd/popover";
import { NzProgressModule } from "ng-zorro-antd/progress";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NzResultModule } from "ng-zorro-antd/result";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzSliderModule } from "ng-zorro-antd/slider";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzStatisticModule } from "ng-zorro-antd/statistic";
import { NzSwitchModule } from "ng-zorro-antd/switch";
import { NzTableModule } from "ng-zorro-antd/table";
import { NzTabsModule } from "ng-zorro-antd/tabs";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzTimePickerModule } from "ng-zorro-antd/time-picker";
import { NzTimelineModule } from "ng-zorro-antd/timeline";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { NzTreeModule } from "ng-zorro-antd/tree";
import { NzTreeSelectModule } from "ng-zorro-antd/tree-select";
import { NzResizableModule } from "ng-zorro-antd/resizable";
import { SoloPartyPipe } from "../helpers/SoloPartyPipe";
import { VisStorageService } from "../services/VisStorageService";
import { SettingsDialogMainTabComponent } from "../dialogs/settingsDialog/tabs/main/main.component";
import { SettingsDialogColorTabComponent } from "../dialogs/settingsDialog/tabs/colors/colors.component";
import { SettingsDialogFflogsTabComponent } from "../dialogs/settingsDialog/tabs/fflogs/fflogs.component";
import { SettingsDialogTableviewTabComponent } from "../dialogs/settingsDialog/tabs/tableview/tableview.component";
import { SettingsDialogTeamworkTabComponent } from "../dialogs/settingsDialog/tabs/teamwork/teamwork.component";
import { SettingsDialogPresetsTabComponent } from "../dialogs/settingsDialog/tabs/presets/presets.component";
import { AddJobComponent } from "../pages/fightline/addJob/addJob.component";
import { JobRolePipe } from "../helpers/JobRolePipe";
import { TableViewOptionsComponent } from "../components/tableviewoptions/tableviewoptions.component";
import { RecentListComponent } from "../components/recent-list/recent-list.component";

import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { MarkdownModule, MARKED_OPTIONS, provideMarkdown } from "ngx-markdown";

const zorroModules = [
  NzAlertModule,
  NzAutocompleteModule,
  NzAvatarModule,
  NzBadgeModule,
  NzButtonModule,
  NzCardModule,
  NzCarouselModule,
  NzCascaderModule,
  NzCheckboxModule,
  NzCollapseModule,
  NzDatePickerModule,
  NzDividerModule,
  NzDrawerModule,
  NzDropDownModule,
  NzEmptyModule,
  NzFormModule,
  NzGridModule,
  NzI18nModule,
  NzIconModule,
  NzInputModule,
  NzInputNumberModule,
  NzLayoutModule,
  NzListModule,
  NzMenuModule,
  NzMessageModule,
  NzModalModule,
  NzNoAnimationModule,
  NzNotificationModule,
  NzPageHeaderModule,
  NzPaginationModule,
  NzPopconfirmModule,
  NzPopoverModule,
  NzProgressModule,
  NzRadioModule,
  NzResultModule,
  NzSelectModule,
  NzSliderModule,
  NzSpaceModule,
  NzSpinModule,
  NzStatisticModule,
  NzSwitchModule,
  NzTableModule,
  NzTabsModule,
  NzTagModule,
  NzTimePickerModule,
  NzTimelineModule,
  NzToolTipModule,
  NzTransButtonModule,
  NzTreeModule,
  NzTreeSelectModule,
  NzWaveModule,
  NzResizableModule,
];

Sentry.init({
  dsn: "https://aa772d49f3bb4a33851f765d5d5f2d86@sentry.io/1407389",
  enabled: environment.production,
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(error) {
    Sentry.captureException(error.originalError || error);
    throw error;
  }
}

export function getBaseUrl() {
  return environment.baseUrl;
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, "assets/i18n/lang_");
}

@NgModule({
  declarations: [
    //    BossTemplateComponent,
    AddJobComponent,
    AppComponent,
    AreaComponent,
    CellComponent,
    CustomScrollDirective,
    DownTimeComponent,
    FFLogsMatcherDirective,
    FightLineComponent,
    FilterComponent,
    FilterComponent,
    HomeComponent,
    JobAbilityComponent,
    JobComponent,
    JobRolePipe,
    KeyHandlerDirective,
    KillsOnlyPipe,
    MultipleAbilityComponent,
    MultipleAttackComponent,
    MultipleDownTimeComponent,
    WarningsComponent,
    NoDraftsPipe,
    OffsetWheelDirective,
    PingComponent,
    PlanAreaComponent,
    SettingsDialogColorTabComponent,
    SettingsDialogFflogsTabComponent,
    SettingsDialogMainTabComponent,
    SettingsDialogPresetsTabComponent,
    SettingsDialogTableviewTabComponent,
    SettingsDialogTeamworkTabComponent,
    SettingsFilterComponent,
    SettingsViewComponent,
    SidepanelComponent,
    SingleAbilityComponent,
    SingleAttackComponent,
    RecentListComponent,
    SoloPartyPipe,
    SyncDowntimeComponent,
    SyncSettingsComponent,
    TableViewComponent,
    TableViewOptionsComponent,
    ToolbarComponent,
    ViewComponent,
    ViewComponent,
    ...DialogsModuleComponents,
  ],
  bootstrap: [AppComponent],
  imports: [
    MarkdownModule.forRoot(),
    AngularSplitModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    ClipboardModule,
    ColorPickerModule,
    CommonModule,
    DragDropModule,
    FormsModule,
    NgProgressbar,
    NgxCaptchaModule,
    PortalModule,
    ReactiveFormsModule,
    VisModule,
    XivapiClientModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ...zorroModules,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: "BASE_URL", useFactory: getBaseUrl },
    { provide: "FFLogs_URL", useValue: "https://www.fflogs.com:443/" },
    { provide: "FFLogs_API_KEY", useValue: "66bfc666827c9b668f4daa87d019e714" },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    {
      provide: NZ_I18N,
      useFactory: () => {
        switch (localStorage.getItem("lang")) {
          case "fr":
            return fr_FR;
          case "de":
            return de_DE;
          case "jp":
          case "ja":
            return ja_JP;
          case "zh":
          case "cn":
            return zh_CN;
          default:
            return en_US;
        }
      },
    },
    ...Services.ServicesModuleComponents,
    VisStorageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideMarkdown({
      loader: HttpClient,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          baseUrl: "/assets/images/changelog/",
        },
      },
    }),
  ],
})
export class AppModule {}
